import { stripTitleEmphasis } from "./title";

const SITE_ORIGIN = "https://lenacareer.ru";

export const SOCIAL_PROFILES = {
  telegram: "https://t.me/lenacareer",
  youtube: "https://youtube.com/c/lenacareer",
  dzen: "https://dzen.ru/lenacareer",
  vk: "https://vk.com/lenacareer",
};

const PERSON_ID = `${SITE_ORIGIN}/#elena`;
const ORGANIZATION_ID = `${SITE_ORIGIN}/#organization`;

/**
 * Elena as a schema.org `Person` with credentials, linked to `/about` — the
 * E-E-A-T author identity reused across every BlogPosting. Her real name (per
 * the site's legal requisites, ИП Маршалова Елена Сергеевна); никаких
 * заявлений о работодателях (memory: client-destination companies are never
 * listed as Elena's employers).
 */
export const BLOG_AUTHOR = {
  "@type": "Person" as const,
  "@id": PERSON_ID,
  name: "Елена Маршалова",
  jobTitle: "Карьерный консультант",
  url: `${SITE_ORIGIN}/about`,
};

const BLOG_PUBLISHER = {
  "@type": "EducationalOrganization" as const,
  "@id": ORGANIZATION_ID,
  name: "Школа карьерного развития «Лена про карьеру»",
  alternateName: "Лена про карьеру",
  url: SITE_ORIGIN,
  logo: {
    "@type": "ImageObject" as const,
    url: `${SITE_ORIGIN}/favicon.svg`,
  },
};

/**
 * Site-wide entity nodes appended to every page's JSON-LD graph, so search
 * engines and LLM crawlers resolve the site, Elena and her channels to one
 * identity (`sameAs`). Facts only — no outcome claims.
 */
export const SITE_ENTITIES_LD: Array<Record<string, unknown>> = [
  {
    "@context": "https://schema.org",
    ...BLOG_AUTHOR,
    image: `${SITE_ORIGIN}/elena-hero.jpg`,
    knowsAbout: [
      "Карьерное консультирование",
      "Составление резюме",
      "Подготовка к собеседованию",
      "Поиск работы",
      "Переговоры о зарплате",
    ],
    worksFor: { "@id": ORGANIZATION_ID },
    sameAs: Object.values(SOCIAL_PROFILES),
  },
  {
    "@context": "https://schema.org",
    ...BLOG_PUBLISHER,
    legalName: "ИП Маршалова Елена Сергеевна",
    taxID: "100123575160",
    founder: { "@id": PERSON_ID },
    hasCredential: {
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "Лицензия на осуществление образовательной деятельности",
      identifier: "Л035-01298-77/02306439",
      dateCreated: "2025-05-14",
      url: `${SITE_ORIGIN}/docs`,
    },
    award: "Знак качества GetCourse — официальный реестр проверенных онлайн-школ GetCourse",
    subjectOf: {
      "@type": "WebPage",
      name: "Карточка школы в реестре Знака качества GetCourse",
      url: "https://getcourse.ru/qualityreestr~dc6a70712a252123c40d2adba6a11d84",
    },
    sameAs: Object.values(SOCIAL_PROFILES),
  },
];

export function toAbsoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${SITE_ORIGIN}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}

export interface BlogPostingLdInput {
  slug: string;
  title: string;
  description: string | null;
  cover: string | null;
  publishedAt: string | null;
  updatedAt: string;
}

export function buildBlogPostingLd(post: BlogPostingLdInput): Record<string, unknown> {
  const url = `${SITE_ORIGIN}/blog/${post.slug}`;
  const ld: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: stripTitleEmphasis(post.title),
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    url,
    author: BLOG_AUTHOR,
    publisher: BLOG_PUBLISHER,
    dateModified: post.updatedAt,
  };
  if (post.description) ld.description = post.description;
  if (post.cover) ld.image = toAbsoluteUrl(post.cover);
  if (post.publishedAt) ld.datePublished = post.publishedAt;
  return ld;
}

export function buildBreadcrumbLd(post: { slug: string; title: string }): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Главная", item: SITE_ORIGIN },
      { "@type": "ListItem", position: 2, name: "Блог", item: `${SITE_ORIGIN}/blog` },
      {
        "@type": "ListItem",
        position: 3,
        name: stripTitleEmphasis(post.title),
        item: `${SITE_ORIGIN}/blog/${post.slug}`,
      },
    ],
  };
}

export function buildFaqLd(
  faq: Array<{ question: string; answer: string }>,
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  };
}
